import create from 'zustand'

import type {
  TPredicateRecommendationResult,
  TWorkBehaviorResult,
  TPerformancePracticeResult,
} from 'types/kinerja-pelaksanaan'
import type { State } from 'zustand'

export interface PenetapanPredikatKinerjaStoreState extends State {
  // -------------------------------------
  // STATES
  // -------------------------------------

  // General states
  // Raw items from API, unfiltered and unpaginated
  initialItems: Array<PenetapanPredikatKinerjaStoreItem>

  // Pagination states
  currentPage: number
  pageSize: number

  // Filter states
  nameFilter: string
  statusPengirimanFilter: StatusPengirimanFilterTypes
  predikatFilter: PredikatFilterTypes
  // Filtered items based on filters criteria
  filteredItems: Array<PenetapanPredikatKinerjaStoreItem>
  // Items that are currently visible based on pagination and filters criteria
  visibleItems: Array<PenetapanPredikatKinerjaStoreItem>

  // Selection states
  selectedItems: Map<string, PenetapanPredikatKinerjaStoreItem>

  // -------------------------------------
  // ACTIONS
  // -------------------------------------

  // General actions
  init: ({
    items,
    page,
    nameFilter,
    statusPengirimanFilter,
    predikatFilter,
  }: {
    items: Array<PenetapanPredikatKinerjaStoreItem>
    page?: number
    nameFilter?: string
    statusPengirimanFilter?: StatusPengirimanFilterTypes
    predikatFilter?: PredikatFilterTypes
  }) => void

  // Pagination actions
  setCurrentPage: (page: number) => void
  setPageSize: (size: number) => void

  // Filter actions
  setNameFilter: (nameFilter: string) => void
  setStatusPengirimanFilter: (
    statusPengirimanFilter: StatusPengirimanFilterTypes
  ) => void
  setPredikatFilter: (predikatFilter: PredikatFilterTypes) => void

  // Selection actions
  selectItem: (item: PenetapanPredikatKinerjaStoreItem) => void
  unselectItem: (ptkId: string) => void
  selectAllItems: () => void
  unselectAllItems: () => void
  isSelectableItem: (item: PenetapanPredikatKinerjaStoreItem) => boolean
}

export type PenetapanPredikatKinerjaStoreItem = {
  id: string
  name: string
  nip: string
  satuanPendidikan: string
  jabatan: string
  perilakuKerja: '' | TWorkBehaviorResult
  praktikKinerja: '' | TPerformancePracticeResult
  predikat: '' | PredikatKinerjaTypes
  statusPengiriman: StatusPengirimanTypes
  meta?: Record<string, any>
}

export type PredikatKinerjaTypes =
  | TPredicateRecommendationResult
  | 'UNFINISHED_PENILAIAN'
  | 'PREDIKAT_NOT_SET'
export type StatusPengirimanTypes = 'SENT' | 'NOT_SENT'
export type StatusPengirimanFilterTypes = StatusPengirimanTypes | 'ALL'
export type PredikatFilterTypes =
  | PenetapanPredikatKinerjaStoreItem['predikat']
  | 'ALL'

export const DEFAULT_PAGE_SIZE = 10
export const DEFAULT_PAGE = 1
export const DEFAULT_STATUS_PENGIRIMAN_FILTER: StatusPengirimanFilterTypes =
  'ALL'
export const DEFAULT_PREDIKAT_FILTER: PredikatFilterTypes = 'ALL'

type CreateStoreOptions = {
  isSelectableFn?: (item: PenetapanPredikatKinerjaStoreItem) => boolean
}

export const createPenetapanPredikatKinerjaStore = (
  options?: CreateStoreOptions
) =>
  create<PenetapanPredikatKinerjaStoreState>((set, get) => {
    const { isSelectableFn = isSelectableItem } = options ?? {}

    // Helper function to filter items based on search criteria, status pengiriman filter, and predikat filter
    const getFilteredItems = (
      items: Array<PenetapanPredikatKinerjaStoreItem>,
      nameFilter: string,
      statusPengirimanFilter: StatusPengirimanFilterTypes,
      predikatFilter: PredikatFilterTypes
    ) => {
      let filteredItems = items

      // Apply name search filter
      if (nameFilter.trim()) {
        const searchTerm = nameFilter.toLowerCase().trim()
        filteredItems = filteredItems.filter((item) =>
          item.name.toLowerCase().includes(searchTerm)
        )
      }

      // Apply delivery status filter
      if (statusPengirimanFilter !== DEFAULT_STATUS_PENGIRIMAN_FILTER) {
        filteredItems = filteredItems.filter(
          (item) => item.statusPengiriman === statusPengirimanFilter
        )
      }

      // Apply predikat filter
      if (predikatFilter !== DEFAULT_PREDIKAT_FILTER) {
        filteredItems = filteredItems.filter(
          (item) => item.predikat === predikatFilter
        )
      }

      return filteredItems
    }

    // Helper function to update visible items with filtering and pagination
    const updateVisibleItems = (
      updates: {
        currentPage?: number
        pageSize?: number
        nameFilter?: string
        statusPengirimanFilter?: StatusPengirimanFilterTypes
        predikatFilter?: PredikatFilterTypes
      } = {}
    ) => {
      const currentState = get()

      // Use provided values or fall back to current state
      const initialItems = currentState.initialItems
      const nameFilter = updates.nameFilter ?? currentState.nameFilter
      const statusPengirimanFilter =
        updates.statusPengirimanFilter ?? currentState.statusPengirimanFilter
      const predikatFilter =
        updates.predikatFilter ?? currentState.predikatFilter
      const pageSize = updates.pageSize ?? currentState.pageSize
      const requestedPage = updates.currentPage ?? currentState.currentPage

      // Filter items based on search criteria, delivery status filter, and predikat filter
      const filteredItems = getFilteredItems(
        initialItems,
        nameFilter,
        statusPengirimanFilter,
        predikatFilter
      )

      // Validate and clamp current page to valid range
      const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize))
      const clampedPage = Math.min(Math.max(requestedPage, 1), totalPages)

      // Calculate pagination
      const startIndex = (clampedPage - 1) * pageSize
      const endIndex = startIndex + pageSize
      const visibleItems = filteredItems.slice(startIndex, endIndex)

      // Update state
      const stateUpdate: any = {
        currentPage: clampedPage,
        filteredItems,
        visibleItems,
      }

      // Only include defined updates
      if (updates.pageSize !== undefined)
        stateUpdate.pageSize = updates.pageSize
      if (updates.nameFilter !== undefined)
        stateUpdate.nameFilter = updates.nameFilter
      if (updates.statusPengirimanFilter !== undefined)
        stateUpdate.statusPengirimanFilter = updates.statusPengirimanFilter
      if (updates.predikatFilter !== undefined)
        stateUpdate.predikatFilter = updates.predikatFilter

      set(stateUpdate)
    }

    return {
      // -------------------------------------
      // STATES
      // -------------------------------------

      // General states
      initialItems: [],

      // Pagination states
      currentPage: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,

      // Filter states
      filteredItems: [],
      nameFilter: '',
      statusPengirimanFilter: DEFAULT_STATUS_PENGIRIMAN_FILTER,
      predikatFilter: DEFAULT_PREDIKAT_FILTER,
      visibleItems: [],

      // Selection states
      selectedItems: new Map(),

      // -------------------------------------
      // ACTIONS
      // -------------------------------------

      // General actions
      init: ({
        items,
        page,
        nameFilter,
        statusPengirimanFilter,
        predikatFilter,
      }) => {
        // Ensure the page is a number
        const resolvedPage = Number.isNaN(page) ? DEFAULT_PAGE : page

        set({
          initialItems: items ?? [],
          selectedItems: new Map(),
        })

        updateVisibleItems({
          currentPage: resolvedPage,
          nameFilter: nameFilter ?? '',
          statusPengirimanFilter:
            statusPengirimanFilter ?? DEFAULT_STATUS_PENGIRIMAN_FILTER,
          predikatFilter: predikatFilter ?? DEFAULT_PREDIKAT_FILTER,
        })
      },

      // Pagination actions
      setCurrentPage: (page: number) => {
        updateVisibleItems({
          currentPage: page,
        })
      },
      setPageSize: (size: number) => {
        updateVisibleItems({
          pageSize: size,
          currentPage: 1, // Reset to first page when page size changes
        })
      },

      // Filter actions
      setNameFilter: (nameFilter: string) => {
        updateVisibleItems({
          nameFilter,
          currentPage: 1, // Reset to first page when search changes
        })
      },
      setStatusPengirimanFilter: (
        statusPengirimanFilter: StatusPengirimanFilterTypes
      ) => {
        updateVisibleItems({
          statusPengirimanFilter,
          currentPage: 1, // Reset to first page when filter changes
        })
      },
      setPredikatFilter: (predikatFilter: PredikatFilterTypes) => {
        updateVisibleItems({
          predikatFilter,
          currentPage: 1, // Reset to first page when filter changes
        })
      },

      // Selection actions
      selectItem: (item: PenetapanPredikatKinerjaStoreItem) => {
        if (!isSelectableFn(item)) return

        const { selectedItems } = get()
        const newSelectedItems = new Map(selectedItems)

        set({
          selectedItems: newSelectedItems.set(item.id, item),
        })
      },

      unselectItem: (ptkId: string) => {
        const { selectedItems } = get()

        const newSelectedItems = new Map(selectedItems)
        newSelectedItems.delete(ptkId)

        set({
          selectedItems: newSelectedItems,
        })
      },

      selectAllItems: () => {
        const { visibleItems } = get()
        const selectableItems = visibleItems.filter(isSelectableFn)
        const newSelectedItems = new Map(
          selectableItems.map((item) => [item.id, item])
        )
        set({
          selectedItems: newSelectedItems,
        })
      },

      unselectAllItems: () => {
        const emptySelectedItems = new Map()
        set({
          selectedItems: emptySelectedItems,
        })
      },

      isSelectableItem: isSelectableFn,
    }
  })

function isSelectableItem(item: PenetapanPredikatKinerjaStoreItem) {
  return (
    item.statusPengiriman === 'NOT_SENT' &&
    item.predikat !== 'UNFINISHED_PENILAIAN'
  )
}
